/**
 * Analytics Service
 * 
 * Domain service for logging analytics and error tracking to Firestore.
 * Includes batching to minimize Firestore writes.
 * 
 * Architecture:
 * - Lives in domain layer as it contains business logic
 * - Uses firestoreService from data layer for persistence
 * - Works with DTOs (data transfer objects) for Firestore interaction
 */

import { firestoreService } from '@/data/services/firestoreService';
import { AnalyticsLogRemapper } from '@/domain/remappers/analytics/analyticsLogRemapper';
import type { AnalyticsLogEntity, LogLevel, DeviceInfo } from '@/domain/entities/analytics/analyticsLogEntity';
import { parseFirstCallerFrame, type ParsedFrame } from '@/domain/services/analytics/stackFrameParser';
import { computeFingerprint } from '@/domain/services/analytics/fingerprint';
import { subscribeToBeforeUnload } from '@/infrastructure/beforeUnload';

const LOGGER_INTERNAL_FILES = ['appLogger', 'analyticsService', 'stackFrameParser', 'globalErrorHandlers'];

/**
 * Optional fields for a log call. Bundled into one object so callers can't
 * confuse `context` (extra data shown in the analytics row) with `error`
 * (the Error instance whose `.stack` populates the log's stack trace).
 */
export interface LogOptions {
  context?: Record<string, any>;
  error?: Error;
  /** Fallback used when the caller has a more reliable source than the captured stack (e.g. `ErrorEvent.filename:lineno`). */
  origin?: string;
}

/**
 * Convert a value into something Firestore can serialize:
 * - Error instances become `{ name, message, stack }` plain objects.
 * - Arrays and plain objects are walked recursively.
 * - Primitives, null, and Date pass through unchanged.
 * Firestore rejects custom class instances; this avoids the "Function
 * <class>.toString() ..." failure when an Error sneaks into `context`.
 */
function sanitizeForFirestore(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack };
  }
  if (value instanceof Date) return value;
  if (Array.isArray(value)) return value.map(sanitizeForFirestore);
  if (typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const k of Object.keys(value as object)) {
      result[k] = sanitizeForFirestore((value as Record<string, unknown>)[k]);
    }
    return result;
  }
  return value;
}

class AnalyticsService {
  private logQueue: AnalyticsLogEntity[] = [];
  private flushTimer: NodeJS.Timeout | null = null;
  private sessionId: string;
  private userIdProvider: (() => string | null) | null = null;
  private readonly BATCH_SIZE = 10;
  private readonly FLUSH_INTERVAL = 5000; // 5 seconds
  private readonly COLLECTION = 'analytics';
  private readonly APP_VERSION = '1.0.0'; // TODO: Get from package.json or config

  constructor() {
    // Generate session ID once per session
    this.sessionId = this.generateSessionId();

    // Flush on page unload. `subscribeToBeforeUnload` is the shared
    // infrastructure surface (SSR-defensive on its own — no local `window`
    // guard needed) importable from the domain layer without crossing the
    // inward-only dependency arrow.
    subscribeToBeforeUnload(() => this.flush());
  }

  /**
   * Get device information
   */
  private getDeviceInfo(): DeviceInfo {
    const ua = navigator.userAgent;
    const isIos = /iPhone|iPad|iPod/.test(ua);
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

    return {
      userAgent: ua,
      platform: navigator.platform,
      screenSize: `${window.screen.width}x${window.screen.height}`,
      isMobile,
      isIos,
    };
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Register a provider that resolves the current user ID at log time.
   *
   * The provider is invoked on every log call so logout/login transitions
   * are reflected immediately. Kept as inverted dependency to avoid the
   * domain layer importing the Redux store from `presentation/` — and to
   * sidestep the circular import that would form via `authSlice` → `appLogger`.
   */
  public setUserIdProvider(provider: () => string | null): void {
    this.userIdProvider = provider;
  }

  /**
   * Get current user ID from the registered provider.
   * Returns null when no provider is set or when the provider throws.
   */
  private getUserId(): string | null {
    if (!this.userIdProvider) return null;
    try {
      return this.userIdProvider();
    } catch {
      return null;
    }
  }

  /**
   * Log a message.
   *
   * Optional fields are bundled into a single `options` object so callers
   * can't accidentally swap `context` and `error` — `appLogger.error(msg,
   * src, error)` was a common mistake that pushed an Error into Firestore's
   * `context` field and broke the analytics batch write.
   */
  public log(
    level: LogLevel,
    message: string,
    source: string,
    options?: LogOptions,
  ): void {
    const context = options?.context;
    const error = options?.error;

    // Debug and info logs are console-only — never sent to Firestore.
    // Routing high-volume info traffic (e.g. per-video init/playback events
    // under feed scrolling) to analytics would balloon Firestore cardinality.
    // Only `warning` and `error` reach the analytics pipeline.
    if (level === 'debug' || level === 'info') {
      const logMethod = level === 'debug' ? console.log : console.info;
      logMethod(`[${level.toUpperCase()}] [${source}]`, message, context || '');
      return;
    }

    // Sanitize any nested Error instances in context — Firestore rejects
    // custom class instances ("Function ...toString() ..."). Errors get
    // flattened to plain `{ name, message, stack }` objects.
    const sanitizedContext = context ? sanitizeForFirestore(context) as Record<string, any> : undefined;

    const frame: ParsedFrame | undefined = error?.stack
      ? parseFirstCallerFrame(error.stack, LOGGER_INTERNAL_FILES)
      : parseFirstCallerFrame(new Error().stack, LOGGER_INTERNAL_FILES);
    const origin = frame
      ? `${frame.file}:${frame.line}`
      : options?.origin;
    const errorName = error?.name;
    const rawCode = (error as any)?.code;
    const errorCode = typeof rawCode === 'string' && rawCode.length < 64 ? rawCode : undefined;
    // Skip fingerprint for non-errors to keep cardinality down.
    const fingerprint = level === 'error'
      ? computeFingerprint({
        source,
        message,
        topFrame: frame ? { file: frame.file, line: frame.line } : undefined,
      })
      : undefined;

    const logEntry: AnalyticsLogEntity = {
      level,
      userId: this.getUserId(),
      message,
      source,
      context: sanitizedContext,
      stackTrace: error?.stack,
      origin,
      fingerprint,
      errorName,
      errorCode,
      deviceInfo: this.getDeviceInfo(),
      timestamp: Date.now(),
      timestampString: new Date().toISOString(),
      sessionId: this.sessionId,
      appVersion: this.APP_VERSION,
    };

    // Add to queue
    this.logQueue.push(logEntry);

    // Console log for development. Only warning/error reach here — debug
    // and info already returned above.
    if (process.env.NODE_ENV === 'development') {
      const logMethod = level === 'error' ? console.error : console.warn;
      logMethod(`[${level.toUpperCase()}] [${source}]`, message, context || '');
    }

    // Flush if batch size reached
    if (this.logQueue.length >= this.BATCH_SIZE) {
      this.flush();
    } else {
      this.scheduleFlush();
    }
  }

  /**
   * Convenience methods
   */
  public debug(message: string, source: string, options?: LogOptions): void {
    this.log('debug', message, source, options);
  }

  public info(message: string, source: string, options?: LogOptions): void {
    this.log('info', message, source, options);
  }

  public warning(message: string, source: string, options?: LogOptions): void {
    this.log('warning', message, source, options);
  }

  public error(message: string, source: string, options?: LogOptions): void {
    this.log('error', message, source, options);
  }

  /**
   * Schedule a flush
   */
  private scheduleFlush(): void {
    if (this.flushTimer) return;

    this.flushTimer = setTimeout(() => {
      this.flush();
    }, this.FLUSH_INTERVAL);
  }

  /**
   * Flush logs to Firestore
   */
  public async flush(): Promise<void> {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    if (this.logQueue.length === 0) return;

    // Get logs to upload
    const logsToUpload = [...this.logQueue];
    this.logQueue = [];

    // Convert entities to DTOs for data layer
    const dtos = logsToUpload.map(AnalyticsLogRemapper.toDto);

    try {
      // Upload to Firestore as one atomic batch (the happy path).
      await firestoreService.batchWrite(this.COLLECTION, AnalyticsLogRemapper.toJsonList(dtos));
    } catch (error) {
      // The batch is atomic, so a single bad doc rejects everything. Retry
      // per-doc: good entries land, only the offenders get dropped. We do
      // NOT re-queue any failures — Firestore's transport already retries
      // transient errors before surfacing them here, so anything that
      // reaches this catch is bad payload that would loop forever if put
      // back on the queue. We log to console only; routing this through
      // appLogger would re-enter the queue and loop on permission failures.
      console.warn('[analyticsService] batch flush failed, retrying per-doc', error);
      let dropped = 0;
      for (const dto of dtos) {
        try {
          await firestoreService.batchWrite(this.COLLECTION, [AnalyticsLogRemapper.toJson(dto)]);
        } catch (singleError) {
          dropped++;
          console.error('[analyticsService] dropping unwritable log', singleError, { dto });
        }
      }
      if (dropped > 0) {
        console.warn(`[analyticsService] dropped ${dropped}/${dtos.length} unwritable analytics entries`);
      }
    }
  }

  /**
   * Clear all queued logs (useful for testing)
   */
  public clearQueue(): void {
    this.logQueue = [];
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();
